package devxertech.com.pages.blogs.blogPages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.components.text.SpanText
import devxertech.com.pages.blogs.PortableBlock
import devxertech.com.pages.blogs.PortableTextSpan
import devxertech.com.pages.blogs.SanityBlogPost
import devxertech.com.pages.blogs.ArticleSection
import devxertech.com.pages.blogs.blogPages.BlogAboutSection
import devxertech.com.pages.blogs.blogPages.BlogBusiness
import devxertech.com.pages.blogs.blogPages.BusinessUsageWithAI
import devxertech.com.pages.blogs.blogPages.ConclusionSection
import devxertech.com.pages.blogs.blogPages.WhyAiImportantSection
import devxertech.com.pages.blogs.fetchPublishedPost
import devxertech.com.pages.blogs.formatPublishedDate
import devxertech.com.pages.blogs.initialPublishedPost
import devxertech.com.utils.imageLoading
import devxertech.com.pages.commons.CommonHeader
import devxertech.com.section.DesktopNavigationAnchor
import devxertech.com.section.onDesktopNavigationClick
import devxertech.com.pages.dashboard.footerSection.HomepageFooter
import devxertech.com.pages.dashboard.ratingSection.RatingSection
import devxertech.com.seo.PageMetadata
import devxertech.com.seo.PageNoIndex
import devxertech.com.seo.JsonLd
import devxertech.com.seo.blogPostingData
import devxertech.com.theme.toSitePalette
import devxertech.com.utils.Constants
import devxertech.com.utils.Res
import kotlinx.browser.document
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Blockquote
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Iframe

private sealed class ArticleState {
    data object Loading : ArticleState()
    data class Loaded(val post: SanityBlogPost) : ArticleState()
    data object NotFound : ArticleState()
    data object Failed : ArticleState()
}

@Page("/blogs/{slug}")
@Composable
fun SanityBlogArticlePage() {
    val pageContext = rememberPageContext()
    val route = pageContext.route
    if (route.path == "/blogs/" && route.params.getValue("slug").isEmpty()) {
        LaunchedEffect(route.pathQueryAndFragment) {
            pageContext.router.navigateTo(
                "/blogs" + route.pathQueryAndFragment.removePrefix("/blogs/"),
                UpdateHistoryMode.REPLACE,
            )
        }
        return
    }

    val slug = route.params.getValue("slug")
    val breakpoint = rememberBreakpoint()
    val palette = ColorMode.current.toSitePalette()
    var state by remember(slug) {
        mutableStateOf<ArticleState>(initialPublishedPost(slug)?.let(ArticleState::Loaded) ?: ArticleState.Loading)
    }

    ArticleRootBaseHref()

    DisposableEffect(slug) {
        var disposed = false
        fetchPublishedPost(slug)
            .then { post ->
                if (!disposed) state = post?.let(ArticleState::Loaded) ?: ArticleState.NotFound
                Unit
            }
            .catch {
                if (!disposed && state !is ArticleState.Loaded) state = ArticleState.Failed
                null
            }
        onDispose { disposed = true }
    }

    val post = (state as? ArticleState.Loaded)?.post
    val heroUrl = post?.heroImageUrl?.ifBlank { post.coverImageUrl }.orEmpty()
    var heroFailed by remember(heroUrl) { mutableStateOf(false) }
    var heroReady by remember(heroUrl) { mutableStateOf(false) }
    val usingFallbackHero = heroUrl.isBlank() || heroFailed
    val pageTitle = when (state) {
        ArticleState.Loading -> "Loading blog article | DevxerTech"
        ArticleState.NotFound -> "Article not found | DevxerTech"
        ArticleState.Failed -> "Unable to load article | DevxerTech"
        is ArticleState.Loaded -> post?.seoTitle?.takeIf(String::isNotBlank) ?: post?.title.orEmpty()
    }
    val pageDescription = when (state) {
        ArticleState.Loading -> "Loading a DevxerTech blog article."
        ArticleState.NotFound -> "The requested DevxerTech blog article could not be found."
        ArticleState.Failed -> "The requested DevxerTech blog article could not be loaded."
        is ArticleState.Loaded -> post?.seoDescription?.takeIf(String::isNotBlank) ?: post?.excerpt.orEmpty()
    }

    PageMetadata(pageTitle, pageDescription)
    PageNoIndex(state == ArticleState.NotFound || state == ArticleState.Failed)
    JsonLd(data =
        (state as? ArticleState.Loaded)?.post?.let { article ->
            blogPostingData(
                slug = article.slug,
                title = article.title,
                description = article.seoDescription.ifBlank { article.excerpt },
                authorName = article.authorName,
                imageUrls = listOf(article.heroImageUrl, article.coverImageUrl),
                publishedAt = article.publishedAt,
                updatedAt = article.updatedAt,
            )
        }
    )

    Column(
        modifier = Modifier.fillMaxWidth().backgroundColor(palette.bodyBackgroundSecondary.rgb)
            .attrsModifier {
                attr("data-blog-state", when (state) {
                    ArticleState.Loading -> "loading"
                    ArticleState.NotFound -> "not-found"
                    ArticleState.Failed -> "failed"
                    is ArticleState.Loaded -> "loaded"
                })
                attr("data-blog-slug", slug)
                attr("data-blog-updated-at", post?.updatedAt.orEmpty())
                attr("data-blog-hero-ready", (state != ArticleState.Loading && heroReady).toString())
                attr("data-blog-content-status", if (post?.hasRenderableArticleContent() == true) "complete" else "incomplete")
            },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        CommonHeader(
            image = if (usingFallbackHero) Res.Image.blog_header_2 else heroUrl,
            breakpoint = breakpoint,
            headingText = when (state) {
                ArticleState.Loading -> "Loading article..."
                ArticleState.NotFound -> "Article not found"
                ArticleState.Failed -> "Unable to load this article"
                is ArticleState.Loaded -> post?.title.orEmpty()
            },
            descriptionText = post?.let(::articleByline) ?: when (state) {
                ArticleState.Loading -> "Loading published content..."
                ArticleState.NotFound -> "We couldn't find a published article at this address."
                ArticleState.Failed -> "Please try again later."
                is ArticleState.Loaded -> ""
            },
            startProject = false,
            mainHeadingAsH1 = true,
            imageAlt = if (usingFallbackHero) "Artificial intelligence in business" else
                post?.heroImageAlt?.ifBlank { post.title }.orEmpty(),
            loadingSkeleton = state == ArticleState.Loading,
            imageLoaded = state != ArticleState.Loading,
            onImageLoad = { heroReady = true },
            onImageError = { heroFailed = true },
            preserveImageAspectRatio = true,
            compactArticleHero = true,
        )

        ArticleBackToListing(breakpoint)

        when (state) {
            ArticleState.Loading -> ArticleBodySkeleton(breakpoint)
            ArticleState.NotFound -> ArticleStateMessage("We couldn't find a published article at this address.", reserveLoadingSpace = true)
            ArticleState.Failed -> ArticleStateMessage("This article could not be loaded. Please try again later.", reserveLoadingSpace = true)
            is ArticleState.Loaded -> post?.let {
                if (it.articleSections.isNotEmpty()) {
                    LegacyArticleSections(it.articleSections)
                } else {
                    BlogArticleBody(it, breakpoint)
                }
            }
        }

        RatingSection(breakpoint)
        HomepageFooter(breakpoint)
    }
}

@Composable
private fun ArticleBackToListing(breakpoint: Breakpoint) {
    val palette = ColorMode.current.toSitePalette()
    DesktopNavigationAnchor(
        path = "/blogs",
        modifier = Modifier
            .fillMaxWidth(if (breakpoint < Breakpoint.MD) 92.percent else 70.percent)
            .margin(top = 20.px, bottom = 4.px)
            .color(palette.commonSecondaryButtonColor.rgb)
            .fontFamily(Constants.FONT_FAMILY)
            .fontSize(if (breakpoint < Breakpoint.MD) 14.px else 16.px)
    ) {
        SpanText("← Back to all blogs")
    }
}

@Composable
private fun ArticleBodySkeleton(breakpoint: Breakpoint) {
    Column(
        modifier = Modifier.fillMaxWidth(if (breakpoint < Breakpoint.MD) 92.percent else 70.percent)
            .margin(top = 35.px, bottom = 45.px)
            .styleModifier { property("min-height", "100vh") },
        verticalArrangement = Arrangement.spacedBy(18.px)
    ) {
        val paragraphWidths = listOf(
            listOf("100%", "96%", "72%"),
            listOf("100%", "100%", "92%", "64%"),
            listOf("100%", "88%", "58%"),
        )
        paragraphWidths.forEach { paragraph ->
            Column(verticalArrangement = Arrangement.spacedBy(8.px)) {
                paragraph.forEachIndexed { index, width ->
                    Div(attrs = Modifier.fillMaxWidth().height(13.px).styleModifier {
                        property("width", width)
                        property("background-color", if (index == paragraph.lastIndex) "rgba(120,130,145,.12)" else "rgba(120,130,145,.16)")
                        property("border-radius", "5px")
                    }.toAttrs())
                }
            }
        }
    }
}

@Composable
private fun ArticleRootBaseHref() {
    val (base, created) = remember {
        val existing = document.head?.querySelector("base")
        if (existing != null) {
            existing to false
        } else {
            document.createElement("base").also {
                it.setAttribute("href", "/")
                document.head?.appendChild(it)
            } to true
        }
    }
    DisposableEffect(base) {
        onDispose {
            if (created) base.parentNode?.removeChild(base)
        }
    }
}

private fun articleByline(post: SanityBlogPost): String = buildList {
    if (post.authorName.isNotBlank()) add("By: ${post.authorName}")
    if (post.categories.isNotEmpty()) add("Category: ${post.categories.joinToString()}")
    if (post.publishedAt.isNotBlank()) add("Date: ${formatPublishedDate(post.publishedAt)}")
}.joinToString(" | ")

@Composable
private fun ArticleStateMessage(message: String, reserveLoadingSpace: Boolean = false) {
    val palette = ColorMode.current.toSitePalette()
    SpanText(
        text = message,
        modifier = Modifier.fillMaxWidth()
            .padding(leftRight = 8.percent, topBottom = 50.px)
            .styleModifier { if (reserveLoadingSpace) property("min-height", "100vh") }
            .color(palette.commonSecondaryButtonColor.rgb)
    )
}

@Composable
private fun BlogArticleBody(post: SanityBlogPost, breakpoint: Breakpoint) {
    val palette = ColorMode.current.toSitePalette()
    Column(
        modifier = Modifier
            .attrsModifier { attr("data-blog-body", "") }
            .fillMaxWidth(if (breakpoint < Breakpoint.MD) 92.percent else 70.percent)
            .margin(top = 35.px, bottom = 45.px),
        verticalArrangement = Arrangement.spacedBy(18.px)
    ) {
        if (post.body.isEmpty()) {
            ArticleStateMessage("This article doesn't have any content yet.")
        } else {
            PortableTextBody(post.body, breakpoint, palette.commonSecondaryButtonColor.rgb, palette.primary.rgb)
        }
    }
}

@Composable
private fun LegacyArticleSections(sections: List<ArticleSection>) {
    Div(attrs = {
        attr("data-blog-body", "")
        attr("style", "display:contents")
    }) {
        sections.forEach { section -> LegacyArticleSection(section) }
    }
}

private fun SanityBlogPost.hasRenderableArticleContent(): Boolean =
    if (articleSections.isNotEmpty()) articleSections.all {
        it.type in setOf("introCalloutSection", "importancePointsSection", "businessAreasPointsSection", "businessUseSection", "highlightedConclusionSection")
    } && articleSections.any { section ->
        when (section.type) {
            "introCalloutSection" -> section.text.isNotBlank()
            "importancePointsSection", "businessAreasPointsSection" -> section.points.any { it.description.isNotBlank() }
            "businessUseSection" -> section.categories.any { category -> category.points.any(String::isNotBlank) }
            "highlightedConclusionSection" -> section.lead.isNotBlank() || section.highlighted.isNotBlank() || section.trailing.isNotBlank()
            else -> false
        }
    } else body.any { (it.type == "block" && it.spans.any { span -> span.text.isNotBlank() }) || (it.type == "youtubeVideo" && youtubeVideoId(it.youtubeUrl) != null) }

private fun youtubeVideoId(url: String): String? {
    val pattern = Regex("^https?://(?:www\\.|m\\.)?(?:youtube\\.com/watch\\?(?:[^#]*&)?v=([A-Za-z0-9_-]{11})(?:[&#][^\\s]*)?|youtube\\.com/shorts/([A-Za-z0-9_-]{11})(?:[?#][^\\s]*)?|youtu\\.be/([A-Za-z0-9_-]{11})(?:[?#][^\\s]*)?)$")
    return pattern.matchEntire(url)?.groupValues?.drop(1)?.firstOrNull { it.isNotEmpty() }
}

@Composable
private fun YouTubeVideo(block: PortableBlock, textColor: Color, linkColor: Color) {
    val id = youtubeVideoId(block.youtubeUrl)
    val title = block.videoTitle.ifBlank { "YouTube video" }
    var playing by remember(block.key, block.youtubeUrl) { mutableStateOf(false) }
    Div(attrs = Modifier.fillMaxWidth().margin(top = 10.px, bottom = 10.px).toAttrs()) {
        if (id != null) {
            Div(attrs = { attr("style", "position:relative;width:100%;aspect-ratio:16/9;overflow:hidden;background:#151515") }) {
                if (playing) {
                    Iframe(attrs = {
                        attr("src", "https://www.youtube-nocookie.com/embed/$id?autoplay=1")
                        attr("title", title)
                        attr("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share")
                        attr("allowfullscreen", "")
                        attr("style", "position:absolute;inset:0;width:100%;height:100%;border:0")
                    })
                } else {
                    Button(attrs = {
                        attr("type", "button")
                        attr("aria-label", "Play $title")
                        attr("style", "position:absolute;inset:0;width:100%;height:100%;border:0;padding:0;cursor:pointer;background:#151515;color:white;font-size:20px")
                        onClick { playing = true }
                    }) {
                        Img(src = "https://i.ytimg.com/vi/$id/hqdefault.jpg", attrs = { attr("alt", ""); attr("style", "position:absolute;inset:0;width:100%;height:100%;object-fit:cover") })
                        org.jetbrains.compose.web.dom.Span(attrs = { attr("style", "position:relative;display:inline-block;background:#b00020;border-radius:8px;padding:12px 22px") }) { Text("▶ Play") }
                    }
                }
            }
        }
        if (block.videoTitle.isNotBlank()) P(attrs = Modifier.color(textColor).fontSize(14.px).toAttrs()) { Text(block.videoTitle) }
        if (block.caption.isNotBlank()) P(attrs = Modifier.color(textColor).fontSize(14.px).toAttrs()) { Text(block.caption) }
        if (id != null) A(href = "https://www.youtube.com/watch?v=$id", attrs = Modifier.color(linkColor).toAttrs { attr("target", "_blank"); attr("rel", "noopener noreferrer") }) { Text("Watch on YouTube") }
    }
}

@Composable
private fun LegacyArticleSection(section: ArticleSection) {
    when (section.type) {
        "introCalloutSection" -> BlogAboutSection(text = section.text)
        "importancePointsSection" -> WhyAiImportantSection(
            heading = section.heading,
            imageUrl = section.imageUrl,
            points = section.points,
        )
        "businessAreasPointsSection" -> BlogBusiness(
            heading = section.heading,
            imageUrl = section.imageUrl,
            points = section.points,
        )
        "businessUseSection" -> BusinessUsageWithAI(
            heading = section.heading,
            imageUrl = section.imageUrl,
            categories = section.categories,
        )
        "highlightedConclusionSection" -> ConclusionSection(
            lead = section.lead,
            highlighted = section.highlighted,
            trailing = section.trailing,
        )
    }
}

@Composable
private fun PortableTextBody(
    blocks: List<PortableBlock>,
    breakpoint: Breakpoint,
    textColor: Color,
    linkColor: Color,
) {
    var index = 0
    while (index < blocks.size) {
        val block = blocks[index]
        if (block.listItem == "bullet" || block.listItem == "number") {
            val listType = block.listItem
            val items = mutableListOf<PortableBlock>()
            while (index < blocks.size && blocks[index].listItem == listType) {
                items += blocks[index]
                index++
            }
            if (listType == "number") {
                Ol(attrs = listModifier(breakpoint, textColor).toAttrs()) {
                    items.forEach { item ->
                        Li { PortableSpans(item.spans, item.markDefs, linkColor) }
                    }
                }
            } else {
                Ul(attrs = listModifier(breakpoint, textColor).toAttrs()) {
                    items.forEach { item ->
                        Li { PortableSpans(item.spans, item.markDefs, linkColor) }
                    }
                }
            }
            continue
        }

        when (block.type) {
            "youtubeVideo" -> YouTubeVideo(block, textColor, linkColor)
            "image" -> if (block.imageUrl.isNotBlank()) {
                Div(attrs = Modifier.fillMaxWidth().margin(top = 10.px, bottom = 10.px).toAttrs()) {
                    Img(src = block.imageUrl, attrs = Modifier.imageLoading(block.imageUrl, lazy = true,
                        sizes = "(max-width: 767px) 92vw, 70vw").toAttrs {
                        attr("alt", block.imageAlt)
                        attr("loading", "lazy")
                        attr("style", "display:block;width:100%;height:auto;object-fit:contain;")
                    })
                    if (block.caption.isNotBlank()) {
                        P(attrs = Modifier.color(textColor).fontSize(14.px).toAttrs()) {
                            Text(block.caption)
                        }
                    }
                }
            }
            "block" -> when (block.style) {
                "h2" -> H2(attrs = headingModifier(breakpoint, textColor, 28).toAttrs()) {
                    PortableSpans(block.spans, block.markDefs, linkColor)
                }
                "h3" -> H3(attrs = headingModifier(breakpoint, textColor, 22).toAttrs()) {
                    PortableSpans(block.spans, block.markDefs, linkColor)
                }
                "blockquote" -> Blockquote(
                    attrs = Modifier
                        .margin(top = 12.px, bottom = 12.px)
                        .padding(left = 20.px)
                        .color(textColor)
                        .fontFamily(Constants.FONT_FAMILY)
                        .fontSize(if (breakpoint < Breakpoint.MD) 16.px else 18.px)
                        .lineHeight(1.6)
                        .toAttrs()
                ) { PortableSpans(block.spans, block.markDefs, linkColor) }
                else -> P(attrs = paragraphModifier(breakpoint, textColor).toAttrs()) {
                    PortableSpans(block.spans, block.markDefs, linkColor)
                }
            }
        }
        index++
    }
}

@Composable
private fun PortableSpans(
    spans: List<PortableTextSpan>,
    markDefs: Map<String, String>,
    linkColor: Color,
) {
    spans.forEach { span ->
        val decorators = span.marks.filter { it == "strong" || it == "em" }
        val link = span.marks.firstNotNullOfOrNull(markDefs::get)
        val marks = decorators + if (link == null) emptyList() else listOf("link")
        PortableMarkedText(span.text, marks, 0, link, linkColor)
    }
}

@Composable
private fun PortableMarkedText(
    text: String,
    marks: List<String>,
    index: Int,
    href: String?,
    linkColor: Color,
) {
    val ctx = rememberPageContext()
    if (index >= marks.size) {
        Text(text)
        return
    }
    when (marks[index]) {
        "strong" -> B { PortableMarkedText(text, marks, index + 1, href, linkColor) }
        "em" -> Em { PortableMarkedText(text, marks, index + 1, href, linkColor) }
        "link" -> if (!href.isNullOrBlank()) {
            val localPath = devxerInternalPath(href)
            val target = localPath ?: href
            val linkModifier = Modifier.color(linkColor).let { modifier ->
                if (localPath == null) modifier
                else modifier.onDesktopNavigationClick { ctx.router.navigateTo(localPath) }
            }
            A(href = target, attrs = linkModifier.toAttrs()) {
                PortableMarkedText(text, marks, index + 1, href, linkColor)
            }
        } else {
            PortableMarkedText(text, marks, index + 1, href, linkColor)
        }
        else -> PortableMarkedText(text, marks, index + 1, href, linkColor)
    }
}

private fun devxerInternalPath(href: String): String? = try {
    val url: dynamic = js("new URL(href, window.location.href)")
    if (url.origin as String != "https://devxertech.com") null
    else "${url.pathname as String}${url.search as String}${url.hash as String}"
} catch (_: Throwable) {
    null
}

private fun headingModifier(
    breakpoint: Breakpoint,
    textColor: Color,
    desktopSizePx: Int,
) = Modifier
    .margin(top = 18.px, bottom = 4.px)
    .color(textColor)
    .fontFamily(Constants.FONT_FAMILY)
    .fontSize(if (breakpoint < Breakpoint.MD) 23.px else desktopSizePx.px)
    .fontWeight(FontWeight.Bold)

private fun paragraphModifier(
    breakpoint: Breakpoint,
    textColor: Color,
) = Modifier
    .margin(top = 0.px, bottom = 8.px)
    .color(textColor)
    .fontFamily(Constants.FONT_FAMILY)
    .fontSize(if (breakpoint < Breakpoint.MD) 15.px else 18.px)
    .lineHeight(1.65)

private fun listModifier(
    breakpoint: Breakpoint,
    textColor: Color,
) = paragraphModifier(breakpoint, textColor).padding(left = 28.px)
